import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';

// Minimal top-nav scoped to /free-tools. Duplicates structure from
// HomePageRedesign's inline Navigation on purpose — the design agent calls
// out that extracting a shared MarketingNav would violate the no-impact
// guardrail for this ticket. See ROADMAP for the future extraction.
// Exported so the analyzer page can use the exact same nav — keeps the
// /free-tools/* surfaces visually consistent without touching the real
// HomePageRedesign Navigation.
export function FreeToolsNav() {
  const navigate = useNavigate();
  return (
    <nav
      className="bg-white/95 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50"
      data-testid="free-tools-nav"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2" data-testid="free-tools-home-link">
            <img
              src="https://customer-assets.emergentagent.com/job_agent-portal-27/artifacts/azdcmpew_Logo_with_brown_background-removebg-preview.png"
              alt="I Need Numbers"
              className="h-8 w-auto"
            />
            <span
              className="text-lg font-bold text-[#2FA163] tracking-wide"
              style={{ fontFamily: 'Playfair Display, Georgia, serif' }}
            >
              I NEED NUMBERS
            </span>
          </Link>

          <div className="hidden md:flex items-center space-x-6">
            <button
              type="button"
              onClick={() => navigate('/features')}
              className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium"
            >
              Features
            </button>
            <span className="text-[#16a34a] text-sm font-semibold">Free Tools</span>
            <button
              type="button"
              onClick={() => navigate('/pricing')}
              className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium"
            >
              Pricing
            </button>
            <button
              type="button"
              onClick={() => navigate('/blog')}
              className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium"
            >
              Blog
            </button>
            <button
              type="button"
              onClick={() => navigate('/support')}
              className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium"
            >
              Support
            </button>
            <button
              type="button"
              onClick={() => navigate('/auth/register')}
              className="bg-[#16a34a] hover:bg-[#0d7a36] text-white font-semibold px-5 py-2 rounded-lg transition-all"
              data-testid="free-tools-nav-trial-btn"
            >
              Start Free Trial
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default function FreeToolsPage() {
  return (
    <div className="min-h-screen flex flex-col bg-gray-50" data-testid="free-tools-page">
      <Helmet>
        <title>Free Tools for Real Estate Agents · I Need Numbers</title>
        <meta
          name="description"
          content="Free, login-free tools for real estate agents. Compare brokerages and team splits side by side with the Broker & Team Analyzer."
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Free Tools for Real Estate Agents · I Need Numbers" />
        <meta
          property="og:description"
          content="Compare brokerages side by side, model splits, and see your real take-home. Free. No login."
        />
      </Helmet>

      <FreeToolsNav />

      <main className="flex-1">
        <section className="max-w-6xl mx-auto px-6 pt-16 pb-8">
          <div className="text-center">
            <div className="text-xs uppercase tracking-wider text-[#16a34a] font-semibold mb-2">
              Free · No login · No email
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
              Free tools for real estate agents
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Standalone calculators that run entirely in your browser. Your numbers
              stay yours — nothing is saved, nothing is sent to us.
            </p>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-6 pb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link
              to="/free-tools/broker-team-analyzer"
              data-testid="free-tools-broker-analyzer-tile"
              className="group block bg-white rounded-xl border border-gray-200 hover:border-[#16a34a] hover:shadow-lg transition-all p-6"
            >
              <div className="flex items-start gap-4 mb-3">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold bg-gradient-to-br from-[#16a34a] to-[#0d7a36] shrink-0">
                  B&amp;T
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-[#0d7a36] transition-colors">
                    Broker &amp; Team Analyzer
                  </h2>
                  <div className="text-xs uppercase tracking-wider text-[#16a34a] font-semibold">
                    Desktop only · ~5 min
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Compare up to 5 brokerages and team splits side by side. See your
                annual take-home, effective split, cap-hit month, and per-deal net —
                so you can pick on numbers, not a pitch.
              </p>
              <div className="text-sm font-semibold text-[#16a34a] group-hover:text-[#0d7a36]">
                Open the analyzer →
              </div>
            </Link>

            <Link
              to="/free-tools/investor-deal-analyzer"
              data-testid="free-tools-investor-analyzer-tile"
              className="group block bg-white rounded-xl border border-gray-200 hover:border-[#16a34a] hover:shadow-lg transition-all p-6"
            >
              <div className="flex items-start gap-4 mb-3">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold bg-gradient-to-br from-[#16a34a] to-[#0d7a36] shrink-0">
                  IDA
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-[#0d7a36] transition-colors">
                    Investor Deal Analyzer
                  </h2>
                  <div className="text-xs uppercase tracking-wider text-[#16a34a] font-semibold">
                    Desktop or mobile · ~2 min
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Run the numbers on an investment property — cash flow, cap rate,
                cash-on-cash return, and break-even — in under a minute. Speak
                investor-client language with confidence.
              </p>
              <div className="text-sm font-semibold text-[#16a34a] group-hover:text-[#0d7a36]">
                Open the calculator →
              </div>
            </Link>

            <Link
              to="/free-tools/seller-net-sheet"
              data-testid="free-tools-seller-net-sheet-tile"
              className="group block bg-white rounded-xl border border-gray-200 hover:border-[#16a34a] hover:shadow-lg transition-all p-6"
            >
              <div className="flex items-start gap-4 mb-3">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold bg-gradient-to-br from-[#16a34a] to-[#0d7a36] shrink-0">
                  SNS
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-[#0d7a36] transition-colors">
                    Seller Net Sheet
                  </h2>
                  <div className="text-xs uppercase tracking-wider text-[#16a34a] font-semibold">
                    Desktop or mobile · ~2 min
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Estimate a seller&rsquo;s net proceeds after commission, loan payoff,
                and closing costs. One screen, every deduction itemized — the number
                you&rsquo;d walk a seller through at the listing appointment.
              </p>
              <div className="text-sm font-semibold text-[#16a34a] group-hover:text-[#0d7a36]">
                Open the calculator →
              </div>
            </Link>

            <Link
              to="/free-tools/mortgage-affordability"
              data-testid="free-tools-mortgage-affordability-tile"
              className="group block bg-white rounded-xl border border-gray-200 hover:border-[#16a34a] hover:shadow-lg transition-all p-6"
            >
              <div className="flex items-start gap-4 mb-3">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold bg-gradient-to-br from-[#16a34a] to-[#0d7a36] shrink-0">
                  M&amp;A
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-[#0d7a36] transition-colors">
                    Mortgage &amp; Affordability
                  </h2>
                  <div className="text-xs uppercase tracking-wider text-[#16a34a] font-semibold">
                    Desktop or mobile · ~3 min
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Estimate the monthly payment and what your buyer can afford —
                including taxes, insurance, HOA, and CDD fees. PITI breakdown, DTI
                check, and max purchase price in one screen.
              </p>
              <div className="text-sm font-semibold text-[#16a34a] group-hover:text-[#0d7a36]">
                Open the calculator →
              </div>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
